/**
 * The one place a plotted series gets its colour.
 *
 * Both charts draw from this. Before it existed, the Budgets lane hashed probe names
 * into a private pastel array while `budget-lanes.ts` assigned lane hues from the
 * `--series-*` tokens — two palettes in one chart, and the drawn one was the
 * unvalidated pastels.
 *
 * Slot order is evidence, not taste. The dataviz validator
 * (`scripts/validate_palette.js --pairs all`, dark surface `#14181d`) passes every
 * check for the first three slots, which is the honest test for a stacked bar where a
 * sparse day can put any two segments against each other. No four-hue subset of the
 * six tokens clears all-pairs CVD separation — the best available is ΔE 2.6 against a
 * 6–8 floor — so slots four to six depend on the secondary encoding these charts ship
 * anyway: a constant series order, a named legend, 2px surface gaps and a tooltip
 * naming every mark. Purple is last because it is the worst offender against slot one
 * (ΔE 2.4 protan). Past the last slot a series folds into a neutral `Other` rather
 * than inventing a hue.
 *
 * `tokens.css` reserves amber, `--good` and `--danger` for status; none of them may
 * carry series identity.
 */

export const CHART_SERIES_SLOTS = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
  'var(--series-3)',
] as const;

export const CHART_OTHER_COLOUR = 'var(--text-4)';

/** Colour-coded series available before the tail has to fold into `Other`. */
export const MAX_CHART_SERIES = CHART_SERIES_SLOTS.length;

/**
 * Assign a colour to each key by its position in the *name-sorted* list.
 *
 * Colour follows the entity, never its rank: a filter that changes which series are
 * visible, or a machine that has a quiet week, must not repaint the survivors.
 */
export function assignSeriesColours(keys: readonly string[]): Map<string, string> {
  const ordered = [...keys].sort((a, b) => a.localeCompare(b));
  const colours = new Map<string, string>();
  ordered.forEach((key, index) => {
    colours.set(key, CHART_SERIES_SLOTS[index] ?? CHART_OTHER_COLOUR);
  });
  return colours;
}
